import { Injectable, inject } from '@angular/core';
import { EventBusService, EventPayload } from 'src/app/shared/services/event-bus.service';
import { LocumsMarginCalculatorEvents } from '../enums/locums-margin-calculator-events';
import { AdditionalRatesStore } from './additional-rates.store';
import { AdditionalRates } from '../../models/additional-rates.models';
import { AdditionalRateTypes } from '../../enums/additional-rate-types';

@Injectable({ providedIn: 'root' })
export class AdditionalRatesReducer {
    private readonly eventBus = inject(EventBusService);
    private readonly additionalRatesStore = inject(AdditionalRatesStore);

    constructor() {
        this.listenToAdditionalRatesEvent();
        this.listenToAdditionalRateFieldsEvent();
    }

    private listenToAdditionalRatesEvent() {
        this.eventBus.on(LocumsMarginCalculatorEvents.ADDITIONAL_RATE_ADDED).subscribe((event: EventPayload) => {
            const additionalRate: AdditionalRates = {
                additionalRateId: null,
                additionalRateTypeId: event.payload.additionalRateTypeId as number,
                marginCoverageType: null,
                billRate: null,
                payRate: null,
                expectedShifts: null,
                expectedHours: null,
                revenue: null,
                expense: null
            };

            this.additionalRatesStore.addAdditionalRate(additionalRate);
        });

        this.eventBus.on(LocumsMarginCalculatorEvents.ADDITIONAL_RATE_REMOVED).subscribe((event: EventPayload) => {
            this.additionalRatesStore.removeAdditionalRate(event.payload.additionalRateTypeId as number);
        });
    }

    private listenToAdditionalRateFieldsEvent() {
        this.listenToAdditionalRateDaily24HoursFieldsEvent();
        this.listenToAdditionalRateWeekendCallFieldsEvent();
        this.listenToAdditionalRateNightShiftFieldsEvent();
        this.listenToAdditionalRateCallBackFieldsEvent();
    }

    private listenToAdditionalRateCallBackFieldsEvent() {
        this.eventBus
            .on(LocumsMarginCalculatorEvents.ADDITIONAL_RATE_CALL_BACK_BILL_RATE_CHANGED)
            .subscribe((event: EventPayload) => {
                this.additionalRatesStore.updateAdditionalRateField({
                    additionalRateTypeId: AdditionalRateTypes.CALL_BACK,
                    billRate: event.payload.billRate as number
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.ADDITIONAL_RATE_CALL_BACK_PAY_RATE_CHANGED)
            .subscribe((event: EventPayload) => {
                this.additionalRatesStore.updateAdditionalRateField({
                    additionalRateTypeId: AdditionalRateTypes.CALL_BACK,
                    payRate: event.payload.payRate as number
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.ADDITIONAL_RATE_CALL_BACK_EXPD_HOURS_CHANGED)
            .subscribe((event: EventPayload) => {
                this.additionalRatesStore.updateAdditionalRateField({
                    additionalRateTypeId: AdditionalRateTypes.CALL_BACK,
                    expectedHours: event.payload.expectedHours as number
                });
            });
    }

    private listenToAdditionalRateNightShiftFieldsEvent() {
        this.eventBus
            .on(LocumsMarginCalculatorEvents.ADDITIONAL_RATE_NIGHT_SHIFT_CALL_MARGIN_COVERAGE_TYPE_CHANGED)
            .subscribe((event: EventPayload) => {
                this.additionalRatesStore.updateAdditionalRateField({
                    additionalRateTypeId: AdditionalRateTypes.NIGHT_SHIFT_CALL,
                    marginCoverageType: event.payload.marginCoverageType as string
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.ADDITIONAL_RATE_NIGHT_SHIFT_CALL_BILL_RATE_CHANGED)
            .subscribe((event: EventPayload) => {
                this.additionalRatesStore.updateAdditionalRateField({
                    additionalRateTypeId: AdditionalRateTypes.NIGHT_SHIFT_CALL,
                    billRate: event.payload.billRate as number
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.ADDITIONAL_RATE_NIGHT_SHIFT_CALL_PAY_RATE_CHANGED)
            .subscribe((event: EventPayload) => {
                this.additionalRatesStore.updateAdditionalRateField({
                    additionalRateTypeId: AdditionalRateTypes.NIGHT_SHIFT_CALL,
                    payRate: event.payload.payRate as number
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.ADDITIONAL_RATE_NIGHT_SHIFT_CALL_EXPD_SHIFTS_CHANGED)
            .subscribe((event: EventPayload) => {
                this.additionalRatesStore.updateAdditionalRateField({
                    additionalRateTypeId: AdditionalRateTypes.NIGHT_SHIFT_CALL,
                    expectedShifts: event.payload.expectedShifts as number
                });
            });
    }

    private listenToAdditionalRateWeekendCallFieldsEvent() {
        this.eventBus
            .on(LocumsMarginCalculatorEvents.ADDITIONAL_RATE_WEEKEND_CALL_MARGIN_COVERAGE_TYPE_CHANGED)
            .subscribe((event: EventPayload) => {
                this.additionalRatesStore.updateAdditionalRateField({
                    additionalRateTypeId: AdditionalRateTypes.WEEKEND_CALL,
                    marginCoverageType: event.payload.marginCoverageType as string
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.ADDITIONAL_RATE_WEEKEND_CALL_BILL_RATE_CHANGED)
            .subscribe((event: EventPayload) => {
                this.additionalRatesStore.updateAdditionalRateField({
                    additionalRateTypeId: AdditionalRateTypes.WEEKEND_CALL,
                    billRate: event.payload.billRate as number
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.ADDITIONAL_RATE_WEEKEND_CALL_PAY_RATE_CHANGED)
            .subscribe((event: EventPayload) => {
                this.additionalRatesStore.updateAdditionalRateField({
                    additionalRateTypeId: AdditionalRateTypes.WEEKEND_CALL,
                    payRate: event.payload.payRate as number
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.ADDITIONAL_RATE_WEEKEND_CALL_EXPD_SHIFTS_CHANGED)
            .subscribe((event: EventPayload) => {
                this.additionalRatesStore.updateAdditionalRateField({
                    additionalRateTypeId: AdditionalRateTypes.WEEKEND_CALL,
                    expectedShifts: event.payload.expectedShifts as number
                });
            });
    }

    private listenToAdditionalRateDaily24HoursFieldsEvent() {
        this.eventBus
            .on(LocumsMarginCalculatorEvents.ADDITIONAL_RATE_DAILY_24HOURS_MARGIN_COVERAGE_TYPE_CHANGED)
            .subscribe((event: EventPayload) => {
                this.additionalRatesStore.updateAdditionalRateField({
                    additionalRateTypeId: AdditionalRateTypes.DAILY_24HOURS,
                    marginCoverageType: event.payload.marginCoverageType as string
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.ADDITIONAL_RATE_DAILY_24HOURS_BILL_RATE_CHANGED)
            .subscribe((event: EventPayload) => {
                this.additionalRatesStore.updateAdditionalRateField({
                    additionalRateTypeId: AdditionalRateTypes.DAILY_24HOURS,
                    billRate: event.payload.billRate as number
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.ADDITIONAL_RATE_DAILY_24HOURS_PAY_RATE_CHANGED)
            .subscribe((event: EventPayload) => {
                this.additionalRatesStore.updateAdditionalRateField({
                    additionalRateTypeId: AdditionalRateTypes.DAILY_24HOURS,
                    payRate: event.payload.payRate as number
                });
            });

        this.eventBus
            .on(LocumsMarginCalculatorEvents.ADDITIONAL_RATE_DAILY_24HOURS_EXPD_SHIFTS_CHANGED)
            .subscribe((event: EventPayload) => {
                this.additionalRatesStore.updateAdditionalRateField({
                    additionalRateTypeId: AdditionalRateTypes.DAILY_24HOURS,
                    expectedShifts: event.payload.expectedShifts as number
                });
            });
    }
}
